import { CircularProgress, Typography } from '@mui/material';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';

import Configuracao from '../util/Configuracao';
import AutoComplete from './AutoComplete';
import AUTO_TEXTO_CERTIDAO from './certidao/AutoTextoCertidao';
import ReactComponent from './ReactComponent';
import ModalConfirmacao from './sessao/ModalConfirmacao';

class AutoCompleteRedator extends ReactComponent {
    salvandoRedator = false;
    modalConfirmacaoOpen = false;
    redatorPendente = null;

    handleChangeRedator = async (valor) => {
        if (this.salvandoRedator || this.desabilitarRedator) {
            return;
        }

        if (this.registro.certidaoJulgamentoAssinada) {
            if (valor && (await this.autotextoRedatorJaPresente(valor))) {
                this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
                await this.executarAlteracaoRedator(valor, false);
                return;
            }

            runInAction(() => {
                this.redatorPendente = valor;
                this.modalConfirmacaoOpen = true;
            });
            return;
        }

        await this.executarAlteracaoRedator(valor, true);
    };

    callbackCancelar = () => {
        this.modalConfirmacaoOpen = false;
        this.redatorPendente = null;
    };

    confirmarDesbloqueio = () => {
        const redator = this.redatorPendente;
        this.redatorPendente = null;
        this.modalConfirmacaoOpen = false;
        this.executarAlteracaoRedator(redator, true);
    };

    executarAlteracaoRedator = async (valor, isInserirAutotexto) => {
        this.salvandoRedator = true;

        try {
            await this.processarAlteracaoRedator(valor, isInserirAutotexto);
        } catch (error) {
            console.error('Erro ao processar alteração de redator:', error);
            this.mensagemStore.abrirMensagemAlerta(
                'Não foi possível salvar o redator. O texto pode ter sido alterado; verifique antes de continuar.'
            );
        } finally {
            runInAction(() => {
                this.salvandoRedator = false;
            });
        }
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            salvandoRedator: observable,
            modalConfirmacaoOpen: observable,
            redatorPendente: observable,
            registro: computed,
            magistrados: computed,
            desabilitarRedator: computed,
            tootipRedator: computed,
            handleChangeRedator: action,
            callbackCancelar: action,
            confirmarDesbloqueio: action,
            executarAlteracaoRedator: false,
            processarAlteracaoRedator: false,
            aplicarAlteracaoRedator: false,
            removerRedatorETextos: false,
            prepararTextosRedator: false,
            salvarEAtualizarTextosRedator: false,
            atualizarEstadoRedator: false,
            montarTextoAutotextoRedator: false,
            autotextoRedatorJaPresente: false,
            obterDecisaoAtual: false,
            adicionarObservacao: false,
            removerObservacao: false,
            normalizarTexto: false,
            textoContem: false,
            renderModalConfirmacao: false,
            render: false
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get magistrados() {
        if (this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
            return this.dominioStore.opcoesMagistradosComposicao(this.registro);
        }
        return this.dominioStore.opcoesMagistradosAutoComplete(this.registro);
    }

    get desabilitarRedator() {
        return this.registro.processo.origem === Configuracao.ORIGEM_PJE && !!this.registro.movimentosTransferidos;
    }

    get tootipRedator() {
        if (this.registro.processo.origem === Configuracao.ORIGEM_PJE && this.registro.movimentosTransferidos) {
            return 'Não é possível designar redator após enviar os movimentos para o PJe';
        }
        return '';
    }

    montarTextoAutotextoRedator(magistrado) {
        return (
            this.salaSessaoStore.pronomeTratamentoComCargo(magistrado) +
            ' ' +
            magistrado.nome.trim() +
            AUTO_TEXTO_CERTIDAO.REDATOR.texto
        );
    }

    async obterDecisaoAtual() {
        if (this.observableProps.textoAtual !== undefined) {
            return this.observableProps.textoAtual;
        }

        if (this.registro.certidaoGerada) {
            const sessao = this.sessaoStore.sessaoSelecionada;
            const certidaoJSON = await this.certidaoService.recuperarCamposCertidao(
                this.registro,
                sessao.orgaoJudicante.sigla,
                sessao.orgaoJudicante.descricao,
                sessao.tipoJulgamento
            );
            return certidaoJSON?.decisao || '';
        }

        return this.registro.decisao || '';
    }

    async autotextoRedatorJaPresente(magistrado) {
        const textoAutotexto = this.montarTextoAutotextoRedator(magistrado);

        if (
            this.observableProps.textoAtual !== undefined &&
            this.textoContem(this.observableProps.textoAtual, textoAutotexto)
        ) {
            return true;
        }

        const decisaoAtual = await this.obterDecisaoAtual();

        if (this.registro.certidaoGerada) {
            return this.textoContem(decisaoAtual, textoAutotexto);
        }

        return this.textoContem(this.registro.rascunhoCertidao || '', textoAutotexto);
    }

    normalizarTexto(texto) {
        return (texto || '')
            .normalize('NFD')
            .replaceAll(/[\u{300}-\u{36F}]/gu, '')
            .toLowerCase();
    }

    textoContem(base, busca) {
        if (!base || !busca) return false;
        return this.normalizarTexto(base).includes(this.normalizarTexto(busca));
    }

    adicionarObservacao(textoBase, conteudo) {
        const texto = textoBase || '';

        if (this.textoContem(texto, conteudo)) {
            return texto;
        }
        const textoRenumerado = this.salaSessaoStore.renumerarObservacoes(texto);
        const numeroObservacao = this.salaSessaoStore.getQuantidadeObservacoes(texto) + 1;
        return textoRenumerado + '\nObservação ' + numeroObservacao + ': ' + conteudo + '.\n';
    }

    removerObservacao(textoBase, conteudo) {
        if (!textoBase || !this.textoContem(textoBase, conteudo)) {
            return textoBase;
        }

        const regexTodasObservacoes = /\n?Observação \d+: [\s\S]*?(?=\nObservação \d+:|$)/g;
        let novoTexto = textoBase.replaceAll(regexTodasObservacoes, (match) => {
            if (this.textoContem(match, conteudo)) return '';
            return match;
        });

        if (this.textoContem(novoTexto, conteudo)) {
            const indiceNormalizado = this.normalizarTexto(novoTexto).indexOf(this.normalizarTexto(conteudo));

            if (indiceNormalizado !== -1) {
                novoTexto =
                    novoTexto.slice(0, indiceNormalizado) + novoTexto.slice(indiceNormalizado + conteudo.length);
            }
        }
        novoTexto = novoTexto.replaceAll(/\n{3,}/g, '\n\n');

        return this.salaSessaoStore.renumerarObservacoes(novoTexto);
    }

    atualizarEstadoRedator(valor) {
        runInAction(() => {
            this.adicionarObservable(this.registro.processo, 'redator', valor);
            this.registro.processo.redator = valor;

            if (this.registro.detalhes) {
                this.adicionarObservable(this.registro.detalhes, 'redator', valor);
                this.registro.detalhes.redator = valor;
            }
        });
    }

    async prepararTextosRedator(valor) {
        const textoAutotexto = this.montarTextoAutotextoRedator(valor);
        const rascunhoAntigo = this.registro.rascunhoCertidao || '';
        const novoRascunho = this.adicionarObservacao(rascunhoAntigo, textoAutotexto);

        const decisaoAntiga = await this.obterDecisaoAtual();
        const novaDecisao = this.adicionarObservacao(decisaoAntiga, textoAutotexto);

        return {
            rascunhoAntigo,
            decisaoAntiga,
            novoRascunho,
            novaDecisao,
            isRascunhoMudou: novoRascunho !== rascunhoAntigo,
            isDecisaoMudou: novaDecisao !== decisaoAntiga
        };
    }

    async salvarEAtualizarTextosRedator(rascunho, decisao, alterarRascunho, alterarDecisao) {
        if (alterarRascunho) {
            await this.salaSessaoStore.salvarRascunhoCertidao(this.registro, rascunho);
            runInAction(() => (this.registro.rascunhoCertidao = rascunho));
        }

        if (alterarDecisao && this.registro.certidaoGerada) {
            await this.certidaoService.salvarDecisao(this.registro, decisao);

            if (this.props.onChangeTexto) {
                this.props.onChangeTexto(decisao);
            }
        }
    }

    async removerRedatorETextos() {
        const magistradoAntigo = this.registro.processo.redator;
        await this.certidaoService.removerRedator(this.registro);

        if (!magistradoAntigo) return;

        const textoRedatorAntigo = this.montarTextoAutotextoRedator(magistradoAntigo);
        const rascunhoAntigo = this.registro.rascunhoCertidao || '';
        const decisaoAntiga = await this.obterDecisaoAtual();

        const novoRascunho = this.removerObservacao(rascunhoAntigo, textoRedatorAntigo);
        const novaDecisao = this.removerObservacao(decisaoAntiga, textoRedatorAntigo);

        const isRascunhoMudou = novoRascunho !== rascunhoAntigo;
        const isDecisaoMudou = novaDecisao !== decisaoAntiga;

        if (isRascunhoMudou || isDecisaoMudou) {
            if (this.registro.certidaoJulgamentoAssinada) {
                runInAction(() => (this.registro.certidaoJulgamentoAssinada = false));
            }
            if (this.props.callbackDesbloqueio) {
                this.props.callbackDesbloqueio();
            }
            await this.salvarEAtualizarTextosRedator(novoRascunho, novaDecisao, isRascunhoMudou, isDecisaoMudou);
        }

        this.atualizarEstadoRedator(null);
    }

    async processarAlteracaoRedator(valor, isInserirAutotexto) {
        if (valor) {
            await this.aplicarAlteracaoRedator(valor, isInserirAutotexto);
        } else {
            await this.removerRedatorETextos();
        }
    }

    async aplicarAlteracaoRedator(valor, isInserirAutotexto) {
        if (!isInserirAutotexto) {
            await this.certidaoService.salvarRedator(this.registro, valor);
            this.atualizarEstadoRedator(valor);
            return;
        }

        const textos = await this.prepararTextosRedator(valor);

        if (!textos.isRascunhoMudou && !textos.isDecisaoMudou) {
            this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
            await this.certidaoService.salvarRedator(this.registro, valor);
            this.atualizarEstadoRedator(valor);
            return;
        }

        if (this.registro.certidaoJulgamentoAssinada) {
            runInAction(() => (this.registro.certidaoJulgamentoAssinada = false));
        }

        try {
            await this.certidaoService.salvarRedator(this.registro, valor);
            this.atualizarEstadoRedator(valor);

            if (this.props.callbackDesbloqueio) {
                this.props.callbackDesbloqueio();
            }

            await this.salvarEAtualizarTextosRedator(
                textos.novoRascunho,
                textos.novaDecisao,
                textos.isRascunhoMudou,
                textos.isDecisaoMudou
            );
        } catch (error) {
            await this.salvarEAtualizarTextosRedator(
                textos.rascunhoAntigo,
                textos.decisaoAntiga,
                textos.isRascunhoMudou,
                textos.isDecisaoMudou
            );
            throw error;
        }
    }

    renderModalConfirmacao() {
        return this.registro?.certidaoJulgamentoAssinada ? (
            <ModalConfirmacao
                callbackCancelar={this.callbackCancelar}
                callbackConfirmar={this.confirmarDesbloqueio}
                open={this.modalConfirmacaoOpen}
                titulo={'Desbloquear assinatura?'}
            >
                <Typography>Esta alteração vai desbloquear a assinatura. Confirma?</Typography>
            </ModalConfirmacao>
        ) : null;
    }

    render() {
        return (
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <AutoComplete
                    alinhamentoCima
                    dense
                    outlined
                    apresentarTextoSecundario={true}
                    destacarLabel={!!this.registro?.processo?.redator}
                    disabled={this.desabilitarRedator || this.salvandoRedator}
                    label={AUTO_TEXTO_CERTIDAO.REDATOR.legenda}
                    onChange={(option) => this.handleChangeRedator(option?.valor)}
                    options={this.magistrados}
                    style={{ margin: '3px 0px 0px 0px', width: '100%' }}
                    title={this.tootipRedator}
                    value={this.registro?.processo?.redator?.sigla || ''}
                />

                {this.salvandoRedator ? (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: '35px',
                            transform: 'translateY(-50%)',
                            marginTop: '2px',
                            pointerEvents: 'none'
                        }}
                    >
                        <CircularProgress size={20} />
                    </div>
                ) : null}

                {this.renderModalConfirmacao()}
            </div>
        );
    }
}

export default inject('stores')(observer(AutoCompleteRedator));
